import React from "react";
import TableViewOutlinedIcon from "@mui/icons-material/TableViewOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import ProcessGuideDialog from "../../../library/Dialog/ProcessGuideDialog";

const tableGuideSteps = [
  {
    label: "Read the table",
    icon: TableViewOutlinedIcon,
    title: "What to enter and what to check",
    guidelines: [
      {
        label: "Enter only B and C",
        text: "B is income received in Singapore. C is income used overseas without being received here. Leave either amount at 0 if it does not apply.",
      },
      {
        label: "Other amounts are calculated for you",
        text: "A, E and J come from your locked income rows. The remaining columns update when you change B or C.",
      },
      {
        label: "Country and YA Earned",
        text: "Rows are grouped by Income Type. Country shows where the income came from; a blank country appears as Unspecified. YA Earned is the Year of Assessment when the income was earned.",
      },
      {
        label: "Common abbreviations",
        details: [
          { label: "b/f", text: "Brought forward from an earlier year." },
          { label: "c/f", text: "Carried forward to a later year." },
          { label: "Remitted", text: "Received in Singapore." },
        ],
      },
    ],
    reminder:
      "To correct source figures, update Foreign Income or the linked schedule. Unlocking removes the tracker row; after locking it again, re-enter B and C.",
  },
  {
    label: "Income (A-D)",
    icon: PaymentsOutlinedIcon,
    title: "Income received, used and left over",
    guidelines: [
      {
        label: "A - Unremitted b/f / Current YA Income",
        text: "Total income available for this row, including any balance from earlier years. This amount comes from the source figures.",
      },
      {
        label: "B - Received in SG",
        text: "Enter the income received in Singapore.",
      },
      {
        label: "C - Used not Received",
        text: "Enter income used overseas without being received in Singapore.",
      },
      {
        label: "D - Unremitted c/f",
        text: "Income left after subtracting the amounts received and used.",
        details: [{ label: "Calculation", text: "D = A - B - C." }],
      },
    ],
    reminder:
      "For example, if A is 10,000, B is 3,000 and C is 2,000, D is 5,000. B plus C cannot exceed A; the tracker adjusts them if this limit is exceeded.",
  },
  {
    label: "Expenses (E-H)",
    icon: ReceiptLongOutlinedIcon,
    title: "How expenses are shared",
    guidelines: [
      {
        label: "E - Allowable Expenses b/f / Current YA Allowable Expenses",
        text: "Total allowable expenses available for this row, including any balance from earlier years. This amount comes from the source figures.",
      },
      {
        label: "F - Share of Expenses Remitted",
        text: "Expenses allocated to income received in Singapore.",
        details: [{ label: "Calculation", text: "F = (B / A) x E." }],
      },
      {
        label: "G - Share of Expenses for Non-Remitted Income",
        text: "Expenses allocated to income used overseas in C.",
        details: [{ label: "Calculation", text: "G = (C / A) x E." }],
      },
      {
        label: "H - Allowable Expenses c/f",
        text: "Expenses left to carry forward after allocating F and G.",
        details: [{ label: "Calculation", text: "H = E - F - G." }],
      },
    ],
    reminder:
      "These amounts are calculated for you. Expense shares are rounded to whole currency units. If A is 0, F and G are 0.",
  },
  {
    label: "Foreign tax (I-M)",
    icon: AccountBalanceOutlinedIcon,
    title: "Net income and foreign tax",
    guidelines: [
      {
        label: "I - Net Income",
        text: "Income received in Singapore after deducting its share of expenses.",
        details: [{ label: "Calculation", text: "I = B - F." }],
      },
      {
        label: "J - Foreign Tax b/f / Current YA Foreign Tax",
        text: "Total foreign tax available for this row, including any balance from earlier years. This amount comes from the source figures.",
      },
      {
        label: "K - Share of Tax Remitted",
        text: "Foreign tax allocated to income received in Singapore.",
        details: [{ label: "Calculation", text: "K = (B / A) x J." }],
      },
      {
        label: "L - Share of Tax for Non-Remitted Income",
        text: "Foreign tax allocated to income used overseas in C.",
        details: [{ label: "Calculation", text: "L = (C / A) x J." }],
      },
      {
        label: "M - Foreign Tax c/f",
        text: "Foreign tax left to carry forward after allocating K and L.",
        details: [{ label: "Calculation", text: "M = J - K - L." }],
      },
    ],
    reminder:
      "Foreign tax shares are rounded to 2 decimal places. If A or J is 0, K and L are 0. Review FTC claims in the summary below the table.",
  },
];

const TrackerInfo = ({ open, onClose }) => (
  <ProcessGuideDialog
    open={open}
    onClose={onClose}
    title='Unremitted Income Tracker - Table guide'
    titleIcon={TableViewOutlinedIcon}
    steps={tableGuideSteps}
    tabsAriaLabel='Tracker table sections'
    closeTooltip='Close table guide'
  />
);

export default TrackerInfo;
